import { useCallback, useMemo, useState, type ThHTMLAttributes } from 'react';
import type { ColumnsType, ColumnType } from 'antd/es/table';
import type { TableProps } from 'antd';
import './useResizableColumns.css';

const DEFAULT_MIN_WIDTH = 60;
const DEFAULT_COLUMN_WIDTH = 120;
export const RESIZABLE_TABLE_CLASS = 'ipc-resizable-table';

type ResizableHeaderCellProps = ThHTMLAttributes<HTMLTableCellElement> & {
  width?: number;
  minWidth?: number;
  onResize?: (width: number) => void;
  onResizeCommit?: (width: number) => void;
};

function ResizableHeaderCell({
  width,
  minWidth = DEFAULT_MIN_WIDTH,
  onResize,
  onResizeCommit,
  style,
  className,
  children,
  ...rest
}: ResizableHeaderCellProps) {
  if (!width || !onResize) {
    return <th {...rest} className={className} style={style}>{children}</th>;
  }

  const handleMouseDown = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const startX = event.clientX;
    const startWidth = width;
    let lastWidth = startWidth;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      lastWidth = Math.max(minWidth, startWidth + moveEvent.clientX - startX);
      onResize(lastWidth);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      onResizeCommit?.(lastWidth);
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // antd 固定列靠 class + sticky；禁止用 inline position:relative 覆盖，否则表头空白/错位
  const isFixedCell =
    Boolean(className?.includes('ant-table-cell-fix-start')) ||
    Boolean(className?.includes('ant-table-cell-fix-end')) ||
    Boolean(className?.includes('ant-table-cell-fix-left')) ||
    Boolean(className?.includes('ant-table-cell-fix-right')) ||
    style?.position === 'sticky' ||
    style?.position === 'fixed';

  return (
    <th
      {...rest}
      className={className}
      style={{
        ...style,
        ...(isFixedCell ? {} : { position: 'relative' as const }),
        width,
        minWidth: width,
        maxWidth: width,
      }}
    >
      {children}
      <span
        className="ipc-resizable-table__handle"
        onMouseDown={handleMouseDown}
        onClick={event => event.stopPropagation()}
      />
    </th>
  );
}

function getColumnKey<T>(column: ColumnType<T>, index: number): string {
  if (column.key != null) return String(column.key);
  if (Array.isArray(column.dataIndex)) return column.dataIndex.join('.');
  if (column.dataIndex != null) return String(column.dataIndex);
  return String(index);
}

function getDefaultWidth<T>(column: ColumnType<T>): number {
  return typeof column.width === 'number' ? column.width : DEFAULT_COLUMN_WIDTH;
}

export type UseResizableColumnsOptions = {
  minWidth?: number;
  /** 受控列宽；配合 onControlledWidthsChange 用于持久化场景 */
  controlledWidths?: Record<string, number>;
  onControlledWidthsChange?: (widths: Record<string, number>) => void;
  /** 拖拽结束时的最终列宽 */
  onResizeCommit?: (columnKey: string, width: number) => void;
  /** 返回 false 时不显示 resize 手柄 */
  isResizable?: (columnKey: string) => boolean;
};

export function useResizableColumns<T>(
  baseColumns: ColumnsType<T>,
  options?: UseResizableColumnsOptions,
) {
  const minWidth = options?.minWidth ?? DEFAULT_MIN_WIDTH;
  const isControlled = options?.onControlledWidthsChange != null;
  const [internalWidthMap, setInternalWidthMap] = useState<Record<string, number>>({});
  const widthMap = isControlled ? (options.controlledWidths ?? {}) : internalWidthMap;

  const handleResize = useCallback((columnKey: string) => (nextWidth: number) => {
    if (isControlled) {
      options.onControlledWidthsChange!({ ...widthMap, [columnKey]: nextWidth });
      return;
    }
    setInternalWidthMap(prev => ({ ...prev, [columnKey]: nextWidth }));
  }, [isControlled, options, widthMap]);

  const hasFixedColumn = useMemo(
    () =>
      baseColumns.some(col => {
        const fixed = col.fixed;
        return fixed === true || fixed === 'left' || fixed === 'right' || fixed === 'start' || fixed === 'end';
      }),
    [baseColumns],
  );

  const hasResizedColumns = Object.keys(widthMap).length > 0;
  const isColumnResizable = options?.isResizable;

  const columns = useMemo(
    () => baseColumns.map((column, index) => {
      const columnKey = getColumnKey(column, index);
      const width = widthMap[columnKey] ?? getDefaultWidth(column);
      const originalOnHeaderCell = column.onHeaderCell;
      const canResize = isColumnResizable ? isColumnResizable(columnKey) : true;

      return {
        ...column,
        width,
        onHeaderCell: (...args: Parameters<NonNullable<typeof originalOnHeaderCell>>) => {
          const originalProps = originalOnHeaderCell?.(...args) ?? {};
          return {
            ...originalProps,
            width,
            minWidth,
            onResize: canResize ? handleResize(columnKey) : undefined,
            onResizeCommit: canResize && options?.onResizeCommit
              ? (nextWidth: number) => options.onResizeCommit!(columnKey, nextWidth)
              : undefined,
          };
        },
      };
    }),
    [baseColumns, widthMap, minWidth, handleResize, isColumnResizable, options?.onResizeCommit],
  );

  const components = useMemo<TableProps['components']>(() => ({
    header: {
      cell: ResizableHeaderCell,
    },
  }), []);

  const totalWidth = useMemo(
    () => columns.reduce((sum, column) => sum + (typeof column.width === 'number' ? column.width : DEFAULT_COLUMN_WIDTH), 0),
    [columns],
  );

  // 无固定列且未手动调宽时不设 scroll.x，避免表格默认出现横向滚动条
  const scrollX = useMemo(() => {
    if (!hasFixedColumn && !hasResizedColumns) return undefined;
    return totalWidth;
  }, [hasFixedColumn, hasResizedColumns, totalWidth]);

  const getScroll = useCallback(
    (extra?: NonNullable<TableProps['scroll']>) => ({
      ...extra,
      ...(extra?.x == null && scrollX != null ? { x: scrollX } : {}),
    }),
    [scrollX],
  );

  return {
    columns,
    components,
    tableLayout: 'fixed' as const,
    scrollX,
    getScroll,
    tableClassName: RESIZABLE_TABLE_CLASS,
  };
}
